import React from "react";
import { Accordion, Badge, Card, Col, Row } from "react-bootstrap";

const normalizeRows = (rows) => {
  if (Array.isArray(rows)) return rows;
  if (typeof rows !== "string") return [];

  try {
    const parsedRows = JSON.parse(rows);
    return Array.isArray(parsedRows) ? parsedRows : [];
  } catch (error) {
    return [];
  }
};

const normalizeOption = (option) => String(option ?? "").trim();

const toSafeNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Math.max(numberValue, 0) : 0;
};

const isEnabledFlag = (value) => [true, 1, "1", "true"].includes(value);

const getFieldOptions = (field, vacancyRows) => {
  const configuredRows = Array.isArray(field?.vacancyOptions)
    ? field.vacancyOptions
    : [];
  const fieldRows = vacancyRows.filter(
    (row) => String(row?.field_id ?? row?.fieldId) === String(field.inputID),
  );
  const options = Array.isArray(field?.options) ? field.options : [];

  return options
    .map(normalizeOption)
    .filter((option, index, allOptions) => option && allOptions.indexOf(option) === index)
    .map((option) => {
      const row = fieldRows.find(
        (candidate) => normalizeOption(candidate?.option_value ?? candidate?.option) === option,
      );
      const fallback = configuredRows.find(
        (candidate) => normalizeOption(candidate?.option_value ?? candidate?.option) === option,
      );
      const totalAvailable = toSafeNumber(
        row?.total_available
          ?? row?.totalAvailable
          ?? fallback?.total_available
          ?? fallback?.totalAvailable,
      );
      const occupied = toSafeNumber(
        row?.used ?? fallback?.used ?? fallback?.associatedTotal,
      );
      const remaining = row?.remaining !== undefined && row?.remaining !== null
        ? toSafeNumber(row.remaining)
        : Math.max(totalAvailable - occupied, 0);

      return {
        option,
        totalAvailable,
        occupied,
        remaining,
      };
    });
};

const getAvailabilityStatus = ({ totalAvailable, occupied, remaining }) => {
  if (totalAvailable <= 0) {
    return { label: "Não configurada", variant: "secondary" };
  }
  if (occupied > totalAvailable) {
    return { label: "Excedida", variant: "danger" };
  }
  if (remaining <= 0 || occupied >= totalAvailable) {
    return { label: "Lotada", variant: "danger" };
  }
  if (occupied / totalAvailable >= 0.8) {
    return { label: "Próxima do limite", variant: "warning" };
  }
  return { label: "Disponível", variant: "success" };
};

const getTotals = (options) => options.reduce(
  (totals, option) => ({
    totalAvailable: totals.totalAvailable + option.totalAvailable,
    occupied: totals.occupied + option.occupied,
    remaining: totals.remaining + option.remaining,
  }),
  { totalAvailable: 0, occupied: 0, remaining: 0 },
);

const getCapacityStatus = ({ totalAvailable, occupied, enabled = true }) => {
  if (!enabled) {
    return { label: "Desativado", variant: "secondary" };
  }
  if (totalAvailable <= 0) {
    return { label: "Não configurado", variant: "secondary" };
  }
  if (occupied > totalAvailable) {
    return { label: "Excedida", variant: "danger" };
  }
  if (occupied >= totalAvailable) {
    return { label: "Lotada", variant: "danger" };
  }
  if (occupied / totalAvailable >= 0.8) {
    return { label: "Próxima do limite", variant: "warning" };
  }
  return { label: "Disponível", variant: "success" };
};

const getPercentage = (current, total) => {
  if (total <= 0) return 0;
  return Math.min(Math.round((current / total) * 100), 100);
};

export const getEventSubscriptionCapacity = (dataTable) => {
  const config = dataTable?.json?.subscriptionLimit || {};
  const enabled = isEnabledFlag(config.status);
  const limit = toSafeNumber(config.limit);
  const used = toSafeNumber(dataTable?.total_participantes);
  return {
    enabled,
    limit,
    used,
    remaining: Math.max(limit - used, 0),
    percentage: enabled ? getPercentage(used, limit) : 0,
    status: getCapacityStatus({ totalAvailable: limit, occupied: used, enabled }),
  };
};

const CapacityProgress = ({ percentage, variant, label }) => (
  <div
    className="progress"
    style={{ height: "0.55rem" }}
    role="progressbar"
    aria-label={label}
    aria-valuemin="0"
    aria-valuemax="100"
    aria-valuenow={percentage}
  >
    <div
      className={`progress-bar bg-${variant}`}
      style={{ width: `${percentage}%` }}
    />
  </div>
);

const StatusBadge = ({ status }) => (
  <Badge
    bg={status.variant}
    text={
      status.variant === "warning" || status.variant === "light" ? "dark" : undefined
    }
    className="flex-shrink-0"
  >
    {status.label}
  </Badge>
);

const ReportPanel = ({ embedded, children }) => embedded ? <>{children}</> : (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body className="p-3 p-lg-4">{children}</Card.Body>
  </Card>
);

export const EventVacancyReport = ({ dataTable, showSubscriptionLimit = true, showHeader = true, alwaysShow = false }) => {
  const FieldList = showHeader ? "div" : Accordion;
  const fields = Array.isArray(dataTable?.json?.form?.fields)
    ? dataTable.json.form.fields.filter(
      (field) => field?.type === "select" && isEnabledFlag(field?.vacancyEnabled),
    )
    : [];
  const vacancyRows = normalizeRows(dataTable?.form_field_vacancies);
  const fieldCards = fields
    .map((field) => ({ field, options: getFieldOptions(field, vacancyRows) }))
    .filter(({ options }) => options.length > 0);
  const hasVacancyControl = fieldCards.length > 0;
  const vacancyTotals = fieldCards.reduce(
    (summary, fieldCard) => {
      const fieldTotals = getTotals(fieldCard.options);
      return {
        totalAvailable: summary.totalAvailable + fieldTotals.totalAvailable,
        occupied: summary.occupied + fieldTotals.occupied,
        remaining: summary.remaining + fieldTotals.remaining,
      };
    },
    { totalAvailable: 0, occupied: 0, remaining: 0 },
  );
  const vacancyStatus = getCapacityStatus({
    totalAvailable: vacancyTotals.totalAvailable,
    occupied: vacancyTotals.occupied,
    enabled: fieldCards.length > 0,
  });
  const vacancyPercentage = getPercentage(
    vacancyTotals.occupied,
    vacancyTotals.totalAvailable,
  );

  const {
    enabled: subscriptionEnabled,
    limit: subscriptionLimit,
    used: currentRegistrations,
    remaining: subscriptionRemaining,
    status: subscriptionStatus,
    percentage: subscriptionPercentage,
  } = getEventSubscriptionCapacity(dataTable);
  const hasSubscriptionLimit = showSubscriptionLimit && subscriptionEnabled && subscriptionLimit > 0;

  if (!hasVacancyControl && !hasSubscriptionLimit && !alwaysShow) {
    return null;
  }

  let subtitleText = "Acompanhe a utilização das vagas e do limite geral de inscrições.";
  if (hasVacancyControl && !hasSubscriptionLimit) {
    subtitleText = "Acompanhe a utilização das vagas por opção do formulário.";
  } else if (!hasVacancyControl && hasSubscriptionLimit) {
    subtitleText = "Acompanhe a utilização do limite geral de inscrições do evento.";
  }

  return (
    <section className={`event-registration-management ${showHeader ? "mb-4" : "mb-0"}`} aria-labelledby={showHeader ? "event-registration-management-title" : undefined} aria-label={showHeader ? undefined : "Gestão das inscrições"}>
      {showHeader && <div className="textHeaderAplicativo">
        <h5 id="event-registration-management-title" className="card-title mb-1">
          Gestão das inscrições
        </h5>
      </div>}
      {showHeader && <h6 className="text-muted mt-3 mx-2 mb-3">
        {subtitleText}
      </h6>}

      <Row className={showHeader ? "g-3 mb-3" : "g-0 gy-3"}>
        {!hasVacancyControl && !hasSubscriptionLimit && <Col xs={12}>
          <div className="rounded border p-3 text-muted small">Nenhum controle de vagas ou limite de inscrições foi configurado para este evento.</div>
        </Col>}
        {hasVacancyControl && (
          <Col xs={12}>
          <ReportPanel embedded={!showHeader}>
              <div className={`d-flex justify-content-between align-items-start gap-2 ${showHeader ? "mb-4" : "mb-3"}`}>
                <div>
                  <h6 className="mb-1">Controle de vagas</h6>
                  {showHeader && <p className="text-muted small mb-0">
                    Capacidade configurada por opção do formulário.
                  </p>}
                </div>
                <StatusBadge status={vacancyStatus} />
              </div>

              <div className="d-flex flex-wrap align-items-end gap-4 mb-3">
                <div>
                  <span className="text-muted small d-block">Vagas ocupadas</span>
                  <strong className="fs-4">{vacancyTotals.occupied}</strong>
                  <span className="text-muted"> / {vacancyTotals.totalAvailable}</span>
                </div>
                <div>
                  <span className="text-muted small d-block">Vagas restantes</span>
                  <strong className="fs-4">{vacancyTotals.remaining}</strong>
                </div>
              </div>

              <CapacityProgress
                percentage={vacancyPercentage}
                variant={vacancyStatus.variant}
                label="Ocupação das vagas por opção"
              />
              <div className="d-flex justify-content-between text-muted small mt-2">
                <span>{vacancyPercentage}% utilizado</span>
                <span>{fieldCards.length} {fieldCards.length === 1 ? "campo" : "campos"} monitorado{fieldCards.length === 1 ? "" : "s"}</span>
              </div>

              {fieldCards.length === 0 ? (
                <div className="rounded border p-3 mt-4 small text-muted">
                  Nenhum campo com controle de vagas foi configurado.
                </div>
              ) : (
                <FieldList className={showHeader ? "mt-4" : "mt-3"}>
                  {fieldCards.map(({ field, options }) => {
                    const fieldTotals = getTotals(options);
                    const fieldHeader = (
                      <>
                        <span className="text-start" style={{ minWidth: 0 }}>
                          <span className="d-block text-muted small">Campo de seleção</span>
                          <strong className="text-break">{field.label || `Campo ${field.inputID}`}</strong>
                        </span>
                        <span className="text-muted small flex-shrink-0">
                          {fieldTotals.occupied}/{fieldTotals.totalAvailable} ocupadas
                        </span>
                      </>
                    );
                    const fieldOptions = <Row className="g-2">
                          {options.map((option) => {
                            const status = getAvailabilityStatus(option);
                            const optionPercentage = getPercentage(option.occupied, option.totalAvailable);

                            return (
                              <Col xs={12} md={6} key={`${field.inputID}-${option.option}`}>
                                <div className="rounded border p-2">
                                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                    <span className="small fw-semibold text-break">{option.option}</span>
                                    <StatusBadge status={status} />
                                  </div>
                                  <div className="d-flex justify-content-between text-muted small mb-1">
                                    <span>{option.occupied} ocupadas</span>
                                    <span>{option.remaining} restantes de {option.totalAvailable}</span>
                                  </div>
                                  <CapacityProgress
                                    percentage={optionPercentage}
                                    variant={status.variant}
                                    label={`Ocupação da opção ${option.option}`}
                                  />
                                </div>
                              </Col>
                            );
                          })}
                    </Row>;

                    return showHeader ? (
                      <div className="border-top pt-3 mt-3" key={`vacancy-report-${field.inputID}`}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">{fieldHeader}</div>
                        {fieldOptions}
                      </div>
                    ) : (
                      <Accordion.Item eventKey={String(field.inputID)} key={`vacancy-report-${field.inputID}`} className="bg-transparent">
                        <h6 className="accordion-header mb-0">
                          <Accordion.Button className="p-2 bg-transparent text-body shadow-none">
                            <span className="d-flex flex-grow-1 justify-content-between align-items-center gap-2 me-2">{fieldHeader}</span>
                          </Accordion.Button>
                        </h6>
                        <Accordion.Body className="p-2">{fieldOptions}</Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </FieldList>
              )}
          </ReportPanel>
          </Col>
        )}

        {hasSubscriptionLimit && (
          <Col xs={12}>
          <ReportPanel embedded={!showHeader}>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-4">
                <div>
                  <h6 className="mb-1">Limite de inscrições</h6>
                  <p className="text-muted small mb-0">
                    Capacidade total de participantes no evento.
                  </p>
                </div>
                <StatusBadge status={subscriptionStatus} />
              </div>

              <div className="d-flex flex-wrap align-items-end gap-4 mb-3">
                <div>
                  <span className="text-muted small d-block">Inscritos</span>
                  <strong className="fs-4">{currentRegistrations}</strong>
                </div>
                <div>
                  <span className="text-muted small d-block">Limite total</span>
                  <strong className="fs-4">{subscriptionEnabled ? subscriptionLimit : "—"}</strong>
                </div>
              </div>

              <CapacityProgress
                percentage={subscriptionPercentage}
                variant={subscriptionStatus.variant}
                label="Utilização do limite de inscrições"
              />
              <div className="d-flex justify-content-between text-muted small mt-2">
                <span>{subscriptionEnabled ? `${subscriptionPercentage}% utilizado` : "Sem limite definido"}</span>
                <span>{subscriptionEnabled ? `${subscriptionRemaining} restantes` : "Cadastro sem limite"}</span>
              </div>

              <div className="rounded border p-3 mt-4 small">
                {subscriptionEnabled ? (
                  <>
                    <strong>{subscriptionRemaining}</strong> inscrições ainda podem ser realizadas antes do limite ser atingido.
                  </>
                ) : (
                  <span className="text-muted">
                    O limite geral de inscrições está desativado para este evento.
                  </span>
                )}
              </div>
          </ReportPanel>
          </Col>
        )}
      </Row>
    </section>
  );
};

export default EventVacancyReport;
